// The part rig's skinning, on the GPU, straight into the lifter's vertex buffer.
//
// Why it exists: this is the ONE stage of a body frame that ran per vertex on the CPU.
// Measured on myra_v10 it was 58 ms a frame (clothes 48, body 10) against a 33 ms budget,
// so a walk posed at ~12 Hz while its 30 fps clip and the render loop ran on — the legs
// stepped while the character slid. Everything either side of it was already on-device:
// the jacobians (0.1 ms) and the lift both read `bufVerts`, and the only reason the verts
// came back to the CPU at all was that THREE skins on the CPU.
//
// The maths is partBranchVertices.deform, unchanged — see wgsl/part_skin.wgsl. Two
// implementations of one formula is the drift risk this repo names elsewhere, so they are
// checked against each other ONCE, at build, on real data (`verify`): a layout or
// transpose mistake shows up as millimetres or metres there rather than as a subtly wrong
// body nobody can see is wrong. A branch that fails verification keeps the CPU path.

import partSkinShader from './wgsl/part_skin.wgsl?raw'
import partUnplaceShader from './wgsl/part_unplace.wgsl?raw'

// A part's uniform record, in floats: two mat4 (32) + axes (4) + signs (4) + translation
// (4) + boneOffset (4). vec4-padded because WGSL aligns a struct member to 16 bytes.
const PART_FLOATS = 48
const PART_BYTES = PART_FLOATS * 4

/** Largest disagreement with the CPU skinner we accept, metres. The trained meshes agree
 *  to ~1 mm and the part fits are solved to fractions of it, so 0.1 mm is far below
 *  anything visible and far above f32 rounding across two different orders of operations. */
export const MAX_SKIN_DISAGREEMENT_METRES = 1e-4

/** One branch's skinning on `device`, or null when the plan carries no vertices. */
export function createGpuPartSkinner(device, plan) {
  if (!device || !plan || !plan.count) return null
  const S = GPUBufferUsage.STORAGE
  const DST = GPUBufferUsage.COPY_DST
  const SRC = GPUBufferUsage.COPY_SRC

  const buffer = (label, data, usage = S | DST) => {
    const buf = device.createBuffer({ label: `part_skin_${label}`, size: Math.max(4, data.byteLength), usage })
    device.queue.writeBuffer(buf, 0, data)
    return buf
  }

  // Static inputs.
  const bufBind = buffer('bind', plan.bindPos)
  const bufIndex = buffer('skin_index', plan.skinIndex)
  const bufWeight = buffer('skin_weight', plan.skinWeight)
  const bufMeta = buffer('meta', plan.meta)
  const bufCorr = buffer('corr', plan.corr)

  // Per-part records. The bone offsets are decided here, so the shader can address one
  // flat bone array however many skeletons the parts share.
  const boneCounts = plan.skeletons.map((s) => s.bones.length)
  const boneOffsets = []
  let bones = 0
  for (const count of boneCounts) { boneOffsets.push(bones); bones += count }
  const partData = new ArrayBuffer(plan.parts.length * PART_BYTES)
  const partF32 = new Float32Array(partData)
  const partU32 = new Uint32Array(partData)
  plan.parts.forEach((part, i) => {
    const at = i * PART_FLOATS
    partF32.set(part.bindMatrix, at)
    partF32.set(part.bindMatrixInverse, at + 16)
    partU32[at + 32] = part.axes[0]; partU32[at + 33] = part.axes[1]; partU32[at + 34] = part.axes[2]
    partF32[at + 36] = part.signs[0]; partF32[at + 37] = part.signs[1]; partF32[at + 38] = part.signs[2]
    partF32[at + 40] = part.translation[0]; partF32[at + 41] = part.translation[1]; partF32[at + 42] = part.translation[2]
    partU32[at + 44] = boneOffsets[part.skeleton]
  })
  const bufParts = buffer('parts', new Uint8Array(partData))
  const bufBones = device.createBuffer({ label: 'part_skin_bones', size: Math.max(64, bones * 16 * 4), usage: S | DST })
  const bufOut = device.createBuffer({ label: 'part_skin_out', size: plan.vertexCount * 3 * 4, usage: S | SRC })

  const pipeline = device.createComputePipeline({
    layout: 'auto',
    compute: { module: device.createShaderModule({ code: partSkinShader }), entryPoint: 'main' },
  })
  const bindGroup = device.createBindGroup({
    layout: pipeline.getBindGroupLayout(0),
    entries: [bufBind, bufIndex, bufWeight, bufMeta, bufParts, bufBones, bufCorr, bufOut]
      .map((buffer, binding) => ({ binding, resource: { buffer } })),
  })

  // The LIVE-source path: skin the branch's own deformed vertices instead of the part
  // GLB's bind positions. Built lazily, because most branches never need it — a branch
  // with no deform of its own has nothing but the bind pose to skin.
  let unplacePipeline = null
  let unplaceBindGroup = null
  let sourceVerts = null
  const useSource = (buffer) => {
    if (buffer === sourceVerts) return
    sourceVerts = buffer
    if (!unplacePipeline) {
      unplacePipeline = device.createComputePipeline({
        layout: 'auto',
        compute: { module: device.createShaderModule({ code: partUnplaceShader }), entryPoint: 'main' },
      })
    }
    unplaceBindGroup = device.createBindGroup({
      layout: unplacePipeline.getBindGroupLayout(0),
      entries: [buffer, bufMeta, bufParts, bufCorr, bufBind]
        .map((buf, binding) => ({ binding, resource: { buffer: buf } })),
    })
  }

  let destroyed = false
  return {
    /** The buffer the lifter reads — stable, so a caller may bind it once. */
    get verts() { return bufOut },

    /** Skin at the rig's CURRENT pose. The bone matrices are the only thing that moves:
     *  `skeleton.update()` recomputes them (bone.matrixWorld · boneInverse, which is what
     *  applyBoneTransform composes per vertex), and this rig is not in the render graph,
     *  so nobody else would. */
    /** Skin the branch's OWN vertices (a live branch's deform output) rather than the part
     *  GLB's bind pose. `buffer` is the lifter's vertex buffer; the next `skin()` unplaces
     *  it into bind space first (wgsl/part_unplace.wgsl). Pass null to go back to the bind
     *  positions the plan carries. */
    setSourceVerts(buffer) {
      if (!buffer) { sourceVerts = null; unplaceBindGroup = null; return }
      useSource(buffer)
    },

    skin() {
      if (destroyed) return null
      plan.skeletons.forEach((skeleton, i) => {
        skeleton.update()
        device.queue.writeBuffer(bufBones, boneOffsets[i] * 16 * 4, skeleton.boneMatrices)
      })
      const encoder = device.createCommandEncoder({ label: 'part_skin' })
      if (unplaceBindGroup) {
        // ONE submission with the skin below: the unplace writes exactly the buffer the
        // skin reads, so splitting them would only add a fence between two passes that
        // are already ordered.
        const un = encoder.beginComputePass({ label: 'part_unplace' })
        un.setPipeline(unplacePipeline)
        un.setBindGroup(0, unplaceBindGroup)
        un.dispatchWorkgroups(Math.ceil(plan.count / 64))
        un.end()
      }
      const pass = encoder.beginComputePass({ label: 'part_skin' })
      pass.setPipeline(pipeline)
      pass.setBindGroup(0, bindGroup)
      pass.dispatchWorkgroups(Math.ceil(plan.count / 64))
      pass.end()
      device.queue.submit([encoder.finish()])
      return bufOut
    },

    /** Skin the live source and report how far the result moved it, metres.
     *
     *  The invariant this checks is that linear blend skinning is the IDENTITY at the rig's
     *  rest pose — the same statement as the part fit's own "neutral mean=0.0mm" — so at
     *  rest, skinning a live branch's own vertices has to hand them back unchanged. It is
     *  the only pose where the answer is known without a second implementation, and it
     *  catches a transposed or mis-scaled inverse in the unplace pass, which would
     *  otherwise show up as a subtly wrong head nobody can see is wrong.
     *
     *  Source and result are copied in ONE submission, so a face-rig update landing
     *  mid-check cannot make a correct skinner look wrong. */
    async verifyIdentity() {
      if (destroyed || !unplaceBindGroup || !sourceVerts) return null
      const size = bufOut.size
      const stage = (label) => device.createBuffer({
        label: `part_skin_${label}`, size,
        usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
      })
      const before = stage('verify_src')
      const after = stage('verify_out')
      const encoder = device.createCommandEncoder({ label: 'part_skin_verify' })
      encoder.copyBufferToBuffer(sourceVerts, 0, before, 0, Math.min(size, sourceVerts.size))
      for (const [label, pipe, group] of [['part_unplace', unplacePipeline, unplaceBindGroup], ['part_skin', pipeline, bindGroup]]) {
        const pass = encoder.beginComputePass({ label })
        pass.setPipeline(pipe)
        pass.setBindGroup(0, group)
        pass.dispatchWorkgroups(Math.ceil(plan.count / 64))
        pass.end()
      }
      encoder.copyBufferToBuffer(bufOut, 0, after, 0, size)
      plan.skeletons.forEach((skeleton, i) => {
        skeleton.update()
        device.queue.writeBuffer(bufBones, boneOffsets[i] * 16 * 4, skeleton.boneMatrices)
      })
      device.queue.submit([encoder.finish()])
      await Promise.all([before.mapAsync(GPUMapMode.READ), after.mapAsync(GPUMapMode.READ)])
      const a = new Float32Array(before.getMappedRange().slice(0))
      const b = new Float32Array(after.getMappedRange().slice(0))
      before.unmap(); after.unmap(); before.destroy(); after.destroy()
      return skinDisagreement(a, b)
    },

    /** Read the skinned verts back — the build-time parity check only, never per frame. */
    async read() {
      const staging = device.createBuffer({
        label: 'part_skin_read', size: bufOut.size,
        usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
      })
      const encoder = device.createCommandEncoder({ label: 'part_skin_read' })
      encoder.copyBufferToBuffer(bufOut, 0, staging, 0, bufOut.size)
      device.queue.submit([encoder.finish()])
      await staging.mapAsync(GPUMapMode.READ)
      const out = new Float32Array(staging.getMappedRange().slice(0))
      staging.unmap()
      staging.destroy()
      return out
    },

    dispose() {
      if (destroyed) return
      destroyed = true
      for (const buf of [bufBind, bufIndex, bufWeight, bufMeta, bufCorr, bufParts, bufBones, bufOut]) {
        try { buf.destroy() } catch { /* best-effort */ }
      }
    },
  }
}

/** Largest |GPU − CPU| over the two vertex arrays, metres. */
export function skinDisagreement(gpu, cpu) {
  let worst = 0
  const n = Math.min(gpu.length, cpu.length)
  for (let i = 0; i < n; i++) {
    const d = Math.abs(gpu[i] - cpu[i])
    if (d > worst) worst = d
  }
  return worst
}

/**
 * Skin once on both paths and compare. Returns the disagreement in metres, or null when
 * the branch has no GPU skinner. The CPU array is the caller's own `deform` result, taken
 * at the SAME pose — this is the only moment the two ever run together.
 */
export async function verifyGpuSkinner(skinner, cpuVerts) {
  if (!skinner) return null
  skinner.skin()
  return skinDisagreement(await skinner.read(), cpuVerts)
}
